import type { IDate } from "../types/date";

export function normalizeDate(date: unknown): IDate | null {
  if (
    !date ||
    typeof date !== "object" ||
    Array.isArray(date) ||
    !("day" in date) ||
    !("month" in date) ||
    !("year" in date) ||
    !("hour" in date) ||
    !("minute" in date)
  ) {
    return null;
  }

  const raw = date as Record<string, unknown>;

  return {
    day: Number(raw.day) as IDate["day"],
    month: Number(raw.month) as IDate["month"],
    year: Number(raw.year),
    hour: Number(raw.hour) as IDate["hour"],
    minute: Number(raw.minute) as IDate["minute"],
  };
}

export function dateToInputValue(date: IDate | null): string {
  if (!date) {
    return "";
  }

  const day = String(date.day).padStart(2, "0");
  const month = String(date.month).padStart(2, "0");
  const year = String(date.year).padStart(4, "0");
  return `${year}-${month}-${day}`;
}

export function parseInputDate(value: string): IDate | null {
  const raw = value.trim();
  if (!raw) {
    return null;
  }

  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  const jsDate = new Date(year, month - 1, day);
  if (
    Number.isNaN(jsDate.getTime()) ||
    jsDate.getFullYear() !== year ||
    jsDate.getMonth() + 1 !== month ||
    jsDate.getDate() !== day
  ) {
    return null;
  }

  return {
    day: day as IDate["day"],
    month: month as IDate["month"],
    year,
    hour: 0 as IDate["hour"],
    minute: 0 as IDate["minute"],
  };
}

export function parseQuantityInput(value: string): number | null {
  const normalized = value.trim();
  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
}

function toUtcTimestamp(date: IDate): number {
  return Date.UTC(date.year, Number(date.month) - 1, Number(date.day));
}

export function isExpiryBeforeManufacturing(
  manufacturingDate: IDate | null,
  expiryDate: IDate | null,
): boolean {
  if (!manufacturingDate || !expiryDate) {
    return false;
  }

  return toUtcTimestamp(expiryDate) < toUtcTimestamp(manufacturingDate);
}
